import {Divider, FormLabel, Grid, Table, TableBody, TableContainer} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import EcdsObsRow from './ecdsObsRow';
import moment from 'moment';
import config from '../../../../app/config';
import _ from 'lodash';
import {mapObservationFromAPI} from '../observation/obsUtils';
import {ecdsMapAssessment} from './ecds_data';
import {useStylesECDS} from './ecdsStyle';
import {useSelector} from 'react-redux';
import {codedAssessment} from '../../../../store/reducers/dicts';
import InformationBox from '../../../../components/other/informationBox';
import {AgyleTable, createAgyleTableColumn} from '../../../../components/table/AgyleTable';
import EcdsTableHeader from './components/ecdsTableHeader';
import {getTableScrollbar} from '../../../../util/scrollbar';

const COLUMNS = [
    createAgyleTableColumn('no', 'No.', 1, null, 'count', 'notSort', 'medium', 'center', true, false, null, 50, null),
    createAgyleTableColumn('score', 'Score', 2, 'PersonScore', 'string', '', 'medium', 'left', true, false, null, 200, null),
    createAgyleTableColumn('timestamp', 'Timestamp', 3, 'AssessmentToolValidationTimestamp', 'date', '', 'medium', 'left', true, true, null, 200, null),
];

const CodedScoredAssessment = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();
    const {version} = props;

    // Table Data
    const [observation, setObservation] = useState(_.isEmpty(props.obs) ? [] : props.obs);
    const [assessments, setAssessments] = useState([]);

    // ECDS Data Set
    const [obsData, setObsData] = useState([]);
    const [otherAssessmentsData, setOtherAssessmentsData] = useState({delirium: [], rockwood: []});
    const [ecds, setEcds] = useState([]);

    // Dictionaries
    const dictionary = useSelector(codedAssessment);

    useEffect(() => {
        setObservation(props.obs);
        setAssessments([]);
        setEcds([]);
        if (!_.isEmpty(props.obs)) {
            _.forEach(props.obs, obs => {
                const mapped = mapObservationFromAPI(obs);

                addToTable(mapped);
            });
        }
    }, [props.obs]);

    useEffect(() => {
        const rockwood = _.filter(props.patient?.studies?.rockwood?.list, rw => !rw.canceled);
        crateDataTable(rockwood, 'rockwood');
        const delirium = _.filter(props.patient?.studies?.delirium?.list, rw => !rw.canceled);
        crateDataTable(delirium, 'delirium');
    }, [props.patient]);

    useEffect(() => {
        props.setData(ecds);
    }, [ecds]);

    useEffect(() => {
        setEcds([...obsData, ...otherAssessmentsData.delirium, ...otherAssessmentsData.rockwood]);
    }, [obsData, otherAssessmentsData]);

    const createRow = (item, title, field) => {
        if (item?.status === 'completed') {
            let score = item?.score;
            if (field === 'painScore') {
                const {painScore} = item;
                if (painScore >= 7) {
                    score = 3;
                } else if (painScore >= 4) {
                    score = 2;
                } else if (painScore > 0) {
                    score = 1;
                } else {
                    score = 0;
                }
            }

            return {
                field: field,
                name: title,
                score,
                timestamp: item?.acceptDate,
            };
        }
    };

    // Transform data to Table
    const addToTable = (obs) => {
        const respRate = createRow(obs.respiration, 'Respiratory Rate', 'respiration');
        const saturation = createRow(obs.spo2scale, 'Inspired O₂%', obs?.spo2scale?.hypercapnic
            ? 'spo2scale2' : 'spo2scale1');
        const airOrOxygen = createRow(obs.airOxygen, 'SpO₂', 'airOxygen');
        const bloodPressure = createRow(obs.bloodPressure, 'Blood Pressure', 'bloodPressure');
        const pulseRate = createRow(obs.pulseRate, 'Heart Rate', 'pulseRate');
        const consciousness = createRow(obs.consciousness, 'Consciousness Level', 'consciousness');
        const temp = createRow(obs.temperature, 'Temperature', 'temperature');
        const painScore = createRow(obs.painScore, 'Pain Score', 'painScore');
        const totalScore = {
            field: 'total',
            name: 'Total Score',
            score: obs.totalScore,
            timestamp: obs?.completedDate,
        };

        let tests = [
            respRate,
            saturation,
            airOrOxygen,
            bloodPressure,
            pulseRate,
            consciousness,
            temp,
            totalScore,
        ];

        if (version === 'V4') {
            tests.splice(tests.length - 1, 0, painScore);
        }

        tests = _.filter(tests, t => !_.isUndefined(t));
        const obj = {
            id: obs.__id,
            tests: tests,
        };

        createECDSData(tests);

        setAssessments(prevState => [
            ...prevState,
            obj,
        ]);
    };

    // Transform data for ECDS data format
    const createECDSData = (obs) => {
        let arr = [];
        if (!_.isEmpty(obs)) {
            _.forEach(obs, ecds => {
                const map = _.find(ecdsMapAssessment, {obs: ecds.field});
                const dict = _.find(dictionary, {ecdsVersion: version, snomedCode: map?.ecds});

                if (dict) {
                    arr.push({
                        CodedAssessmentToolType_SnomedCt: dict?.snomedCode,
                        PersonScore: ecds.score,
                        AssessmentToolValidationTimestamp: moment(ecds.timestamp).utc().format(config.ecdsDateTimeFormat),
                    });
                }
            });
        }
        setObsData(arr);
    };

    // Find assessments for table
    const findAssessments = (id) => {
        let arr = [];
        if (!_.isEmpty(assessments)) {
            arr = _.find(assessments, {id: id})?.tests;
        }
        return arr || [];
    };

    // Render Table
    const render = () => {
        if (!_.isEmpty(observation)) {
            return (
                <Grid container direction="row">
                    <Grid item xs={12} style={{marginTop: 1}}>
                        <TableContainer className={getTableScrollbar()}>
                            <Table>
                                <EcdsTableHeader/>

                                <TableBody>
                                    {observation.map((obs, index) => (
                                        <EcdsObsRow
                                            assessment
                                            index={index}
                                            key={obs.__id}
                                            obs={{
                                                id: obs.__id,
                                                date: obs.examinationEnd ? moment(obs.examinationEnd).format(config.defaultDateTimeFormat) : '-',
                                                by: obs.completedByName || '-',
                                                status: obs.status ? _.startCase(obs.status) : '-',
                                            }}
                                            innerText="Assessments"
                                            innerColumns={['Name', 'Score', 'Timestamp']}
                                            innerRow={findAssessments(obs.__id)}
                                            editObs={props.editObs}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            );
        } else {
            return (
                <InformationBox type="info">
                    {t('Observations have not been made.')}
                </InformationBox>
            );
        }
    };

    const renderTableOtherAssessments = (data, label) => {
        if (_.isEmpty(data)) return null;

        return (
            <>
                <Divider style={{margin: '10px 0'}}/>

                <FormLabel>
                    {label}
                </FormLabel>

                <AgyleTable
                    edRefresh
                    columns={COLUMNS}
                    data={data}
                    disableFilter
                />
            </>
        );
    };

    const crateDataTable = (items, type) => {
        let data = [];
        const map = _.find(ecdsMapAssessment, {obs: type});
        const dict = _.find(dictionary, {ecdsVersion: version, snomedCode: map?.ecds});
        _.forEach(items, item => {
            data.push({
                CodedAssessmentToolType_SnomedCt: dict?.snomedCode,
                PersonScore: type === 'rockwood' ? (item?.scale?.__code || '0') : (item?.totalScore || '0'),
                AssessmentToolValidationTimestamp: item.data,
            });
        });

        setOtherAssessmentsData(prevState => ({
            ...prevState,
            [type]: data,
        }));
    };

    const renderOtherAssessments = () => {

        return (
            <>
                {renderTableOtherAssessments(otherAssessmentsData.rockwood, t('Clinical Frailty Score'))}
                {renderTableOtherAssessments(otherAssessmentsData.delirium, t('Delirium Screening'))}
            </>
        );
    };

    if (props.accordion !== 'Scored Assessment') return null;

    return (
        <div className={classes.root}>
            {render()}
            {version === 'V4' && renderOtherAssessments()}
        </div>
    );
};

export default CodedScoredAssessment;